import { FollowerDialog } from "@/components/FollowerModal";
import GdOrgSwitchDialog, { useOrgList } from "@/components/GdOrgSwitch";
import GuideTooltip from "@/components/GuideTooltip";
import HBIcon from "@/components/HBIcon";
import HBMessage from "@/components/HBMessage";
import Image from "@/components/Image";
import { useLogin } from "@/components/LoginModal";
import { MuseTag } from "@/components/Muse";
import { AdditionGoodDialog, BuyVipDialog } from "@/components/Vip";
import { useUserJobName } from "@/constants/areas";
import useAppStore from "@/context/store";
import displayNum from "@/utils/displayNum";
import imageUrl, { gdOssUrl } from "@/utils/imageUrl";
import isEditingElement from "@/utils/isEditingElement";
import useOuterClick from "@/utils/useOuterClick";

import { PermissionCodes } from "@/constants/permissionCodes";
import {
  useHover,
  useKeyPress,
  useLocalStorageState,
  useSessionStorageState,
} from "ahooks";
import { Button, Dropdown } from "antd";
import classNames from "classnames";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import Link from "next/link";
import { useRouter } from "next/router";
import { forwardRef, useEffect, useMemo, useRef } from "react";
import { Case, Switch } from "react-if";
import GdPermission from "../../GdPermission";
import { useIsNewHeader } from "../headerContext";
import styles from "./index.module.less";
import { useInitRedTipState, useRedTipState } from "./redTipState";
import { VipPanel } from "./VipPanel";

dayjs.extend(duration);

export default function Avatar({ linkTo, isNewLayout } = {}) {
  const { user, config, unPayOrders } = useAppStore(
    ({ user, config, unPayOrders }) => ({
      user,
      config,
      unPayOrders,
    })
  );
  const isNewHeader = useIsNewHeader();
  const { logout } = useLogin();
  const avatarRef = useRef(null);
  const [dropRef, dropVisible, setDropVisible] = useOuterClick(false, {
    extraTargets: [avatarRef],
  });
  const router = useRouter();

  // 红点标识
  useInitRedTipState();
  const { couponTip, orderTip } = useRedTipState((store) => store);
  const avatarRedTip = useMemo(() => {
    return couponTip || orderTip;
  }, [couponTip, orderTip]);

  const chatDialogRef = useRef();
  // 快捷键：消息弹窗
  useKeyPress(
    "m",
    async (e) => {
      const editing = isEditingElement();
      if (editing || e.shiftKey || e.altKey || e.metaKey || e.ctrlKey) {
        return false;
      }

      if (chatDialogRef.current) {
        chatDialogRef.current.close();
        chatDialogRef.current = null;
      } else {
        const { ChatDialog } = await import("@/components/Chat");
        chatDialogRef.current = ChatDialog.open();
      }
    },
    { exactMatch: true }
  );

  const [hideOrderTooltip, setHideOrderTooltip] =
    useSessionStorageState("showOrderTooltip");

  // const isPro = user?.extra?.pro;

  const avatarImg = useMemo(() => {
    if (user?.avatar) {
      // 目前头像最小是 75宽度
      return imageUrl(user.avatar, { width: 75, rect: "sq" });
    } else {
      return gdOssUrl(
        "https://cdn.dancf.com/fe-assets/img/c62d185173c48b4c73f90ea6b7acee33.png"
      );
    }
  }, [user?.avatar]);

  const timeShowRef = useRef(null);
  const timeHideRef = useRef(null);
  const showDrop = () => {
    // setTipVisible(false);
    clearTimeout(timeShowRef.current);
    clearTimeout(timeHideRef.current);
    timeShowRef.current = setTimeout(() => {
      setDropVisible(true);
    }, 300);
  };

  const hideDrop = () => {
    clearTimeout(timeShowRef.current);
    clearTimeout(timeHideRef.current);
    timeHideRef.current = setTimeout(() => setDropVisible(false), 300);
  };

  return (
    <>
      <Dropdown
        className={styles.avatar}
        getPopupContainer={(triggerNode) => triggerNode.parentElement}
        destroyPopupOnHide
        overlay={
          <AvatarDrop
            user={user}
            config={config}
            avatarImg={avatarImg}
            ref={dropRef}
            onHide={hideDrop}
            onShow={showDrop}
            unPayOrders={unPayOrders}
            logout={logout}
            openBuyVipDialog={() => BuyVipDialog.open()}
            openAddition={AdditionGoodDialog.open}
            linkTo={linkTo}
          />
        }
        visible={dropVisible}
        placement="bottomRight"
      >
        <GuideTooltip
          visible={
            !hideOrderTooltip &&
            unPayOrders.length > 0 &&
            router.asPath !== "/me/order"
          }
          overlayClassName={styles.orderTooltip}
          title={null}
          content={
            <Link href={"/me/order"} passHref>
              <a>
                你有一笔待支付订单， 立即处理 <HBIcon type="ic_chevron_right" />
              </a>
            </Link>
          }
          placement={"bottomLeft"}
          width={"278px"}
          hideAction
          onClose={() => setHideOrderTooltip(true)}
          getPopupContainer={() => avatarRef.current}
        >
          <div
            ref={avatarRef}
            onMouseEnter={showDrop}
            onMouseLeave={hideDrop}
            className="header-item"
            style={{
              padding: isNewHeader ? 4 : "4px 0 4px 4px",
            }}
            data-gd-expose="button_expose"
            data-gd-click="button_click"
            data-module-name="顶部导航栏"
            data-subject-name="按钮"
            data-button-name="个人面板"
          >
            <Link href={linkTo || "/user/" + user?.urlname}>
              <a
                className={classNames(
                  { [styles.unread]: avatarRedTip },
                  styles.avatarWrap
                )}
              >
                <Image
                  src={avatarImg}
                  alt={user?.username}
                  width="32"
                  height="32"
                />
                {/* {!!isPro && (
                  <div className={styles.pro}>
                    <HBIcon
                      type="a-bianzu6"
                      style={{
                        backgroundImage:
                          "radial-gradient(#fff 40%, #ffffff00 20%)",
                      }}
                    />
                  </div>
                )} */}
              </a>
            </Link>

            {!isNewHeader && !isNewLayout && (
              <HBIcon type="ic_arrow_down" className={styles.downicon} />
            )}
          </div>
        </GuideTooltip>
      </Dropdown>
    </>
  );
}

export const AvatarDrop = forwardRef(function RawAvatarDrop(
  { avatarImg, user, config, onHide, onShow, logout, linkTo },
  ref
) {
  const router = useRouter();
  const isHovering = useHover(() =>
    document.querySelector(`.${styles.username}`)
  );
  //const unreadNum = useRedTipState((store) => store.dmUnread);
  const isPro = user?.extra?.pro;

  const [showCertifyTip, setShowCertifyTip] = useLocalStorageState(
    `certifyTip_user_${user?.user_id}`,
    {
      defaultValue: true,
    }
  );
  const [historyTip, setHistoryTip] = useLocalStorageState(
    `_PINIT_FRONT_USER_GUIDE_SIGN_HISTORY_TIP_`,
    {
      defaultValue: true,
    }
  );

  const OptionList = [
    {
      icon: "register",
      name: "花瓣认证",
      url: "/muse/register/",
      iconStyle: {
        fontSize: 19,
        marginTop: 2,
      },
      showRedTip: showCertifyTip,
      action: (evl) => {
        const showRedTip = evl.showRedTip;
        if (showRedTip) {
          setShowCertifyTip(false);
        }
        window.open(evl.url, "_blank");
      },
    },
    {
      icon: "ic_pro",
      name: "花瓣Pro",
      url: isPro ? "https://pro.huaban.com/my/vip" : "https://pro.huaban.com/",
    },
    {
      icon: "ic_gzguanli",
      name: "管理关注",
      url: `/user/${user?.urlname}/following`,
    },
    {
      icon: "ic_settings",
      name: "个人设置",
      url: "/settings",
      isBlank: true,
    },
    {
      icon: "recent_fill",
      name: "浏览记录",
      url: "/history",
      showRedTip: historyTip,
      action: (evl) => {
        const showRedTip = evl.showRedTip;
        if (showRedTip) {
          setHistoryTip(false);
        }
        router.push(evl.url);
      },
    },
    {
      icon: "ic_exit",
      name: "退出花瓣",
      action: () => {
        onHide();
        logout();
      },
    },
  ];

  useEffect(() => {
    if (showCertifyTip) {
      if (!user?.extra?.is_museuser) {
        setShowCertifyTip(true);
      }
    }
  }, [user, showCertifyTip, setShowCertifyTip]);

  const jobName = useUserJobName(user);

  const onUserIdCopy = (id) => {
    navigator.clipboard.writeText(id);
    HBMessage.success("用户ID已复制");
  };

  const isOfficial = useMemo(() => {
    return config?.officialUsers?.split(",").includes(user?.urlname);
  }, [config, user]);

  const { orgList } = useOrgList();

  if (!user) {
    return null;
  }

  // 用户主页链接
  const userHomeLink = linkTo || `/user/${user.urlname}`;

  return (
    <div
      className={styles.avatarDrop}
      ref={ref}
      onMouseLeave={onHide}
      onMouseEnter={onShow}
      data-content-source="个人头像下拉框"
      data-dialog-title="个人头像下拉框"
      data-module-name="个人面板弹窗"
      data-subject-name="弹窗"
      data-gd-expose="dialog_expose"
    >
      <div className={styles.user}>
        <Link href={userHomeLink} passHref>
          <a
            className={classNames(styles.userLink, {
              [styles.hover]: isHovering,
            })}
          >
            <Image src={avatarImg} alt={user.username} width="48" height="48" />
          </a>
        </Link>
        <div className={styles.userInfo}>
          <Link href={userHomeLink} passHref>
            <a className={styles.username}>
              <span className={styles.text}>{user.username}</span>
            </a>
          </Link>
          <Switch>
            <Case condition={isOfficial}>
              <div className={styles.official} title="花瓣官方"></div>
            </Case>
            <Case condition={user?.extra?.is_museuser}>
              <Link href={`/muse/register`} passHref>
                <a className={styles.designer}>
                  <MuseTag className={styles.museTag} user={user} size={20} />
                </a>
              </Link>
            </Case>
          </Switch>
          <Link href={userHomeLink} passHref>
            <a className={styles.userMore}>
              <HBIcon type="ic_chevron_right" />
            </a>
          </Link>
          <span className={styles.job}>
            {jobName || (
              <Link href={"/settings"} passHref>
                <a
                  className={styles.setJob}
                  data-dialog-action="点击-设置职业"
                  data-gd-click="dialog_action"
                >
                  职业未设置
                </a>
              </Link>
            )}
            <span
              className={styles.userId}
              onClick={() => onUserIdCopy(user.user_id)}
            >
              用户ID: {user.user_id} <HBIcon type="copy" />
            </span>
          </span>
        </div>

        {orgList?.length > 1 && (
          <GdPermission code={PermissionCodes.changeOrgTypeWithUser}>
            <Button
              className={styles.switchOrgBtn}
              onClick={() => {
                GdOrgSwitchDialog.open({
                  orgList,
                });
              }}
              data-gdmerge
              data-gd-click="button_click"
              data-button-name="切换组织"
            >
              切换组织
              <HBIcon type="switch" />
            </Button>
          </GdPermission>
        )}
      </div>
      <ul className={styles.info}>
        <li data-dialog-action="点击-画板" data-gd-click="dialog_action">
          <Link href={userHomeLink} passHref>
            <a>
              <span>{displayNum(user.board_count)}</span>
              <span>画板</span>
            </a>
          </Link>
        </li>
        <li data-dialog-action="点击-采集" data-gd-click="dialog_action">
          <Link href={`${userHomeLink}/pins`} passHref>
            <a>
              <span>{displayNum(user.pin_count)}</span>
              <span>采集</span>
            </a>
          </Link>
        </li>

        <li data-dialog-action="点击-关注" data-gd-click="dialog_action">
          <Link href={`/user/${user.urlname}/following`} passHref>
            <a>
              <span>{displayNum(user.following_count)}</span>
              <span>关注</span>
            </a>
          </Link>
        </li>

        <li
          onClick={() => {
            FollowerDialog.open({ urlname: user.urlname });
          }}
          data-dialog-action="点击-粉丝"
          data-gd-click="dialog_action"
        >
          <a>
            <span>{displayNum(user.follower_count)}</span>
            <span>粉丝</span>
          </a>
        </li>
      </ul>
      <VipPanel />
      <div className={styles.operationList}>
        {OptionList.map((item) => {
          // if (item.type === "separate") {
          //   return <div key={index} className="separate"></div>;
          // }
          if (item.action) {
            return (
              <Link href={item.url || ""} key={item.name}>
                <a
                  className={styles.item}
                  onClick={(e) => {
                    e.preventDefault();
                    item.action(item);
                  }}
                  data-dialog-action={`点击-${item.name}`}
                  data-gd-click="dialog_action"
                  key={item.name}
                >
                  {item.icon && (
                    <HBIcon
                      className={classNames({
                        [styles.unread]: !!item.unreadNum,
                        [styles.redTip]: item.showRedTip,
                      })}
                      type={item.icon}
                      style={{
                        fontSize: item.iconSize || "20px",
                        color: item.iconColor || "#505153",
                        ...(item.iconStyle || {}),
                      }}
                    />
                  )}
                  <span>{item.name}</span>
                </a>
              </Link>
            );
          }

          // 其他默认item
          return (
            <Link href={item.url} key={item.name}>
              <a
                className={styles.item}
                target={item.isBlank ? "_blank" : "_self"}
                data-dialog-action={`点击-${item.name}`}
                data-gd-click="dialog_action"
              >
                {item.icon && (
                  <HBIcon
                    type={item.icon}
                    style={{
                      fontSize: item.iconSize || "20px",
                      color: item.iconColor || "#505153",
                      ...(item.iconStyle || {}),
                    }}
                    className={classNames({ [styles.redTip]: item.showRedTip })}
                  />
                )}
                <span>{item.name}</span>
              </a>
            </Link>
          );
        })}
      </div>
    </div>
  );
});
